import type { ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import { Icon, type IconName } from './icon.js';

/**
 * The statuses a post target moves through (PLAN §7.1), plus the two
 * connection states the Accounts screen shows.
 */
export type StatusTone = 'ok' | 'warn' | 'bad' | 'busy' | 'idle';

/**
 * Status is never carried by colour alone — every badge pairs an icon with a
 * word, so it survives both colour blindness and a greyscale screenshot
 * (PLAN §13.1).
 */
const tones: Record<StatusTone, { className: string; icon: IconName }> = {
  ok: { className: 'text-ok bg-ok-soft border-ok/35', icon: 'check' },
  warn: { className: 'text-warn bg-warn-soft border-warn/35', icon: 'alert' },
  bad: { className: 'text-bad bg-bad-soft border-bad/35', icon: 'cross' },
  busy: { className: 'text-accent bg-accent-soft border-accent/35', icon: 'dot' },
  idle: { className: 'text-muted bg-raise border-line', icon: 'dash' },
};

export interface StatusBadgeProps {
  tone: StatusTone;
  children: ReactNode;
  /** Drops the background, for use inside an already-tinted row. */
  plain?: boolean;
  /**
   * Swaps the icon for a breathing dot. For a state that is true *right now*
   * — a live connection — rather than one that finished, which is what the
   * tick says.
   */
  pulse?: boolean;
  className?: string;
}

export function StatusBadge({
  tone,
  children,
  plain = false,
  pulse = false,
  className,
}: StatusBadgeProps) {
  const { className: toneClass, icon } = tones[tone];
  return (
    <span
      className={cx(
        'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-aw border px-2 py-0.5',
        // No nowrap. Short labels — Connected, Published — never wrap anyway
        // because they fit. Callers also pass whole sentences here, and one of
        // those (a 114-character TikTok note) was 761px of unbreakable text
        // inside a 453px column, which gave the entire composer a horizontal
        // scrollbar. A badge must not be able to do that to the page.
        'font-mono text-2xs font-medium break-words',
        plain ? 'border-transparent bg-transparent' : toneClass,
        plain && toneClass.split(' ')[0],
        className,
      )}
    >
      {pulse ? <LiveDot /> : <Icon name={icon} size={12} className="shrink-0" />}
      {children}
    </span>
  );
}

/** A filled dot with a ring expanding out of it, both in the badge colour. */
function LiveDot() {
  return (
    <span aria-hidden="true" className="relative grid size-1.5 shrink-0 place-items-center">
      <span className="aw-pulse-ring absolute size-1.5 rounded-full bg-current" />
      <span className="relative size-1.5 rounded-full bg-current" />
    </span>
  );
}

export interface SeverityRowProps {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
}

/**
 * Dispatch's signature: a severity stripe down the left edge of a row, so a
 * list of targets can be triaged by shape before anything is read.
 */
export function SeverityRow({ tone, children, className }: SeverityRowProps) {
  const stripe: Record<StatusTone, string> = {
    ok: 'border-l-ok',
    warn: 'border-l-warn',
    bad: 'border-l-bad',
    busy: 'border-l-accent',
    idle: 'border-l-line',
  };
  return (
    <div
      className={cx(
        'border-b border-l-[3px] border-b-line bg-surface px-3 py-2.5',
        stripe[tone],
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface ProgressProps {
  /** 0–100. Omit for an indeterminate bar, e.g. a platform still processing. */
  value?: number;
  label: string;
  /** Right-aligned detail: "12.4 MB of 38.2 MB · 340 KB/s". */
  detail?: string;
}

export function Progress({ value, label, detail }: ProgressProps) {
  const determinate = typeof value === 'number';
  const clamped = determinate ? Math.min(100, Math.max(0, value)) : 0;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-ink">{label}</span>
        {detail && <span className="font-mono text-2xs tabular-nums text-muted">{detail}</span>}
      </div>
      {/* A strip of film: two dark rails with sprocket holes rolling between
          them. The shape is doing a job rather than decorating — uploading
          has a real percentage and converting has none, and the moving holes
          are what tells the two apart without a number. A 1.5px bar could
          only sit frozen, which reads as dead rather than busy. */}
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={determinate ? 0 : undefined}
        aria-valuemax={determinate ? 100 : undefined}
        aria-valuenow={determinate ? Math.round(clamped) : undefined}
        aria-valuetext={determinate ? `${Math.round(clamped)}%` : 'Working'}
        className="relative h-6 w-full overflow-hidden rounded-[4px] border-y-[5px] border-ink bg-line"
      >
        <div
          className={cx(
            'absolute inset-0 bg-accent transition-[width] duration-500',
            // Dimmed when there is no percentage: the strip is full because
            // there is nothing to measure, not because the work is finished.
            !determinate && 'opacity-35',
          )}
          style={{ width: determinate ? `${clamped}%` : '100%' }}
        />
        <span
          aria-hidden="true"
          className="aw-sprockets pointer-events-none absolute inset-x-0 inset-y-[-5px]"
        />
      </div>
    </div>
  );
}
